import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, Plus, Trash2, User, FileText, Send } from 'lucide-react';
import SelectCustomerModal from '../manage/SelectCustomerModal';
import SelectServiceModal from '../manage/SelectServiceModal';
import { type Customer } from '../../lib/customers';
import type { Service } from '../../lib/services';
import {
  createStandaloneInvoice,
  updateStandaloneInvoice,
  addInvoiceLine,
  updateInvoiceLine,
  deleteInvoiceLine,
  issueInvoice,
  deleteDraftInvoice,
  getInvoiceWithLines,
  type InvoiceRow,
  type InvoiceLineRow,
  type InvoiceDiscountSource,
} from '../../lib/invoices/standaloneInvoices';

interface InvoiceEditorModalProps {
  businessId: string;
  /** Existing draft to edit, or null to create a new one. */
  invoiceId?: string | null;
  onClose: () => void;
  onSaved: () => void;
  /** Called after the invoice is issued — open the viewer so the user can immediately see/send it. */
  onIssued?: (invoiceId: string) => void;
}

const fmt = (cents: number) => (cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const toCents = (d: number | string) => Math.round((Number(d) || 0) * 100);

const TERMS: { id: string; label: string }[] = [
  { id: 'due_on_receipt', label: 'Due on receipt' },
  { id: 'net_7', label: 'Net 7' },
  { id: 'net_15', label: 'Net 15' },
  { id: 'net_30', label: 'Net 30' },
  { id: 'net_60', label: 'Net 60' },
];

export default function InvoiceEditorModal({ businessId, invoiceId: initialInvoiceId, onClose, onSaved, onIssued }: InvoiceEditorModalProps) {
  const [invoiceId, setInvoiceId] = useState<string | null>(initialInvoiceId ?? null);
  const [invoice, setInvoice] = useState<InvoiceRow | null>(null);
  const [lines, setLines] = useState<InvoiceLineRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [customerPickerOpen, setCustomerPickerOpen] = useState(false);
  const [servicePickerOpen, setServicePickerOpen] = useState(false);

  // Header-edit local state (committed on blur/change).
  const [notes, setNotes] = useState('');
  const [terms, setTerms] = useState('due_on_receipt');
  const [discountType, setDiscountType] = useState<'none' | 'percentage' | 'fixed'>('none');
  const [discountValue, setDiscountValue] = useState('');

  const reload = async (id: string) => {
    const data = await getInvoiceWithLines(id);
    if (data) {
      setInvoice(data.invoice);
      setLines(data.lines);
      setNotes(data.invoice.notes || '');
      setTerms(data.invoice.payment_terms || 'due_on_receipt');
      const ds = data.invoice.discount_source;
      if (ds && ds.type) {
        setDiscountType(ds.type);
        setDiscountValue(ds.type === 'fixed' ? String((ds.value || 0) / 100) : String(ds.value || 0));
      }
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        let id = initialInvoiceId ?? null;
        if (!id) {
          id = await createStandaloneInvoice({ businessId });
        }
        if (!active) return;
        setInvoiceId(id);
        await reload(id);
      } catch (e: any) {
        if (active) setError(e?.message || 'Failed to open invoice.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const withBusy = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try { await fn(); } catch (e: any) { setError(e?.message || 'Something went wrong.'); } finally { setBusy(false); }
  };

  // Custom bill-to: bill someone else without changing the linked customer.
  const [billToEditorOpen, setBillToEditorOpen] = useState(false);
  const [billToDraft, setBillToDraft] = useState({ name: '', email: '', phone: '', address: '' });

  const openBillToEditor = () => {
    setBillToDraft({
      name: invoice?.bill_to_name || '',
      email: invoice?.bill_to_email || '',
      phone: invoice?.bill_to_phone || '',
      address: invoice?.bill_to_address || '',
    });
    setBillToEditorOpen(true);
  };

  const saveBillToDraft = () => {
    if (!invoiceId) return;
    void withBusy(async () => {
      await updateStandaloneInvoice({
        invoiceId,
        customerId: invoice?.customer_id ?? null,
        billToName: billToDraft.name.trim() || null,
        billToEmail: billToDraft.email.trim() || null,
        billToPhone: billToDraft.phone.trim() || null,
        billToAddress: billToDraft.address.trim() || null,
        notes,
        discountSource: currentDiscountSource(),
      });
      await reload(invoiceId);
      setBillToEditorOpen(false);
    });
  };

  const handleSelectCustomer = (c: Customer) => {
    setCustomerPickerOpen(false);
    if (!invoiceId) return;
    const name = `${c.first_name || ''} ${c.last_name || ''}`.trim() || null;
    void withBusy(async () => {
      await updateStandaloneInvoice({
        invoiceId,
        customerId: c.id,
        billToName: name,
        billToEmail: c.email || null,
        billToPhone: c.phone || null,
        billToAddress: (c as any).address || null,
        notes,
        discountSource: currentDiscountSource(),
      });
      await reload(invoiceId);
    });
  };

  const handleAddService = async (service: Service, priceOption: any) => {
    setServicePickerOpen(false);
    if (!invoiceId) return;
    const isPerUnit = priceOption?.pricing_type === 'per_unit' || priceOption?.pricing_type === 'per_sqft';
    const unit = isPerUnit ? (priceOption?.unit_price || 0) : (priceOption?.price || 0);
    const optName = priceOption?.name && priceOption.name !== 'Default' ? ` (${priceOption.name})` : '';
    await withBusy(async () => {
      await addInvoiceLine({
        invoiceId,
        description: `${service.name}${optName}`,
        quantity: 1,
        unitAmountCents: toCents(unit),
        taxable: !service.tax_exempt,
        serviceId: service.id,
        sortOrder: lines.length,
      });
      await reload(invoiceId);
    });
  };

  const handleAddCustomLine = () => {
    if (!invoiceId) return;
    void withBusy(async () => {
      await addInvoiceLine({ invoiceId, description: '', quantity: 1, unitAmountCents: 0, taxable: true, sortOrder: lines.length });
      await reload(invoiceId);
    });
  };

  const commitLine = (line: InvoiceLineRow, patch: Partial<{ description: string; quantity: number; unit: number; taxable: boolean; lineDiscount: number }>) => {
    if (!invoiceId) return;
    void withBusy(async () => {
      await updateInvoiceLine({
        lineId: line.id,
        description: patch.description ?? line.description,
        quantity: patch.quantity ?? line.quantity,
        unitAmountCents: patch.unit !== undefined ? toCents(patch.unit) : line.unit_amount_cents,
        taxable: patch.taxable ?? line.taxable,
        lineDiscountCents: patch.lineDiscount !== undefined ? toCents(patch.lineDiscount) : line.line_discount_cents,
      });
      await reload(invoiceId);
    });
  };

  const removeLine = (lineId: string) => {
    if (!invoiceId) return;
    void withBusy(async () => { await deleteInvoiceLine(lineId); await reload(invoiceId); });
  };

  const currentDiscountSource = (): InvoiceDiscountSource => {
    if (discountType === 'none') return null;
    const v = Number(discountValue) || 0;
    return { type: discountType, value: discountType === 'fixed' ? toCents(v) : v };
  };

  const commitHeader = () => {
    if (!invoiceId) return;
    void withBusy(async () => {
      await updateStandaloneInvoice({
        invoiceId,
        customerId: invoice?.customer_id ?? null,
        billToName: invoice?.bill_to_name ?? null,
        billToEmail: invoice?.bill_to_email ?? null,
        billToPhone: invoice?.bill_to_phone ?? null,
        billToAddress: invoice?.bill_to_address ?? null,
        notes,
        discountSource: currentDiscountSource(),
      });
      await reload(invoiceId);
    });
  };

  const handleDiscard = () => {
    if (!invoiceId) { onClose(); return; }
    // Only auto-discard an untouched empty draft; otherwise keep it as a saved draft.
    void withBusy(async () => {
      if (lines.length === 0 && !invoice?.customer_id) {
        try { await deleteDraftInvoice(invoiceId); } catch { /* ignore */ }
      }
      onClose();
    });
  };

  const handleSaveDraft = () => { commitHeader(); onSaved(); onClose(); };

  const handleIssue = () => {
    if (!invoiceId) return;
    if (lines.length === 0) { setError('Add at least one line item before issuing.'); return; }
    void withBusy(async () => {
      await updateStandaloneInvoice({
        invoiceId,
        customerId: invoice?.customer_id ?? null,
        billToName: invoice?.bill_to_name ?? null,
        billToEmail: invoice?.bill_to_email ?? null,
        billToPhone: invoice?.bill_to_phone ?? null,
        billToAddress: invoice?.bill_to_address ?? null,
        notes,
        discountSource: currentDiscountSource(),
      });
      await issueInvoice(invoiceId, terms);
      onSaved();
      onClose();
      // Hand off to the issued-invoice viewer (PDF / email / text / payment).
      onIssued?.(invoiceId);
    });
  };

  const readOnly = !!invoice && invoice.status !== 'draft';
  const inputCls = 'text-[13px] border border-gray-300 rounded-lg px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 disabled:bg-gray-50 disabled:text-gray-500';

  const content = (
    <div className="fixed inset-0 z-[100040] bg-black/50 flex items-stretch justify-center" onClick={busy ? undefined : handleDiscard}>
      <div className="bg-gray-100 shadow-xl w-full h-full flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-gray-200 bg-white shrink-0" style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}>
          <div className="w-full max-w-3xl mx-auto flex items-center justify-between">
            <div>
              <div className="text-[12px] font-semibold uppercase tracking-wide text-gray-400">New invoice</div>
              <div className="text-[15px] font-semibold text-gray-900">{invoice?.invoice_number ? `Invoice #${invoice.invoice_number}` : 'Invoice'}</div>
            </div>
            <button onClick={handleDiscard} disabled={busy} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 disabled:opacity-50"><X className="h-5 w-5" /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4 w-full max-w-3xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-16 text-gray-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : (
            <>
              {/* Customer */}
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-[13px] font-semibold text-gray-800"><User className="h-4 w-4 text-blue-500" /> Bill to</label>
                  {!readOnly && (
                    <div className="flex items-center gap-3">
                      <button onClick={() => setCustomerPickerOpen(true)} className="text-[12px] font-medium text-blue-600 hover:text-blue-800">{invoice?.customer_id ? 'Change' : 'Select customer'}</button>
                      <button
                        onClick={openBillToEditor}
                        className="text-[12px] font-medium text-blue-600 hover:text-blue-800"
                        title="Bill someone else without changing the customer"
                      >
                        Custom
                      </button>
                    </div>
                  )}
                </div>
                {billToEditorOpen ? (
                  <div className="mt-2 space-y-1.5 max-w-sm">
                    <input value={billToDraft.name} onChange={(e) => setBillToDraft((d) => ({ ...d, name: e.target.value }))}
                      placeholder="Name" className={`${inputCls} w-full`} />
                    <input value={billToDraft.email} onChange={(e) => setBillToDraft((d) => ({ ...d, email: e.target.value }))}
                      placeholder="Email" type="email" className={`${inputCls} w-full`} />
                    <input value={billToDraft.phone} onChange={(e) => setBillToDraft((d) => ({ ...d, phone: e.target.value }))}
                      placeholder="Phone" className={`${inputCls} w-full`} />
                    <input value={billToDraft.address} onChange={(e) => setBillToDraft((d) => ({ ...d, address: e.target.value }))}
                      placeholder="Address" className={`${inputCls} w-full`} />
                    <div className="flex gap-2 pt-1">
                      <button onClick={saveBillToDraft} disabled={busy}
                        className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-[12px] font-semibold disabled:opacity-60">Save</button>
                      <button onClick={() => setBillToEditorOpen(false)} disabled={busy}
                        className="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 text-[12px] font-medium">Cancel</button>
                    </div>
                  </div>
                ) : invoice?.bill_to_name ? (
                  <div className="mt-1.5 text-[13px] text-gray-700">
                    <div className="font-medium text-gray-900">{invoice.bill_to_name}</div>
                    {invoice.bill_to_email && <div className="text-gray-500">{invoice.bill_to_email}</div>}
                    {invoice.bill_to_phone && <div className="text-gray-500">{invoice.bill_to_phone}</div>}
                    {invoice.bill_to_address && <div className="text-gray-500">{invoice.bill_to_address}</div>}
                  </div>
                ) : (
                  <div className="mt-1.5 text-[12px] text-gray-400">No customer selected — pick one or enter custom billing details.</div>
                )}
              </div>

              {/* Line items */}
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="flex items-center gap-2 text-[13px] font-semibold text-gray-800"><FileText className="h-4 w-4 text-blue-500" /> Items</label>
                  {!readOnly && (
                    <div className="flex gap-2">
                      <button onClick={() => setServicePickerOpen(true)} className="inline-flex items-center gap-1 text-[12px] font-medium text-blue-600 hover:text-blue-800"><Plus className="h-3.5 w-3.5" /> Service</button>
                      <button onClick={handleAddCustomLine} className="inline-flex items-center gap-1 text-[12px] font-medium text-blue-600 hover:text-blue-800"><Plus className="h-3.5 w-3.5" /> Custom line</button>
                    </div>
                  )}
                </div>

                {lines.length === 0 ? (
                  <div className="text-[12px] text-gray-400 py-4 text-center">No items yet — add a service or a custom line.</div>
                ) : (
                  <div className="space-y-2">
                    {lines.map((line) => (
                      <div key={line.id} className="rounded-xl border border-gray-200 p-2.5">
                        <div className="flex items-start gap-2">
                          <input
                            defaultValue={line.description}
                            placeholder="Description"
                            disabled={readOnly}
                            onBlur={(e) => { if (e.target.value !== line.description) commitLine(line, { description: e.target.value }); }}
                            className={`${inputCls} flex-1`}
                          />
                          {!readOnly && <button onClick={() => removeLine(line.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>}
                        </div>
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          <label className="text-[11px] text-gray-500">Qty
                            <input type="number" min="0" step="1" defaultValue={line.quantity} disabled={readOnly}
                              onBlur={(e) => { const v = Number(e.target.value) || 0; if (v !== line.quantity) commitLine(line, { quantity: v }); }}
                              className={`${inputCls} w-16 ml-1`} />
                          </label>
                          <label className="text-[11px] text-gray-500">Unit $
                            <input type="number" min="0" step="0.01" defaultValue={(line.unit_amount_cents / 100).toFixed(2)} disabled={readOnly}
                              onBlur={(e) => { const v = Number(e.target.value) || 0; if (toCents(v) !== line.unit_amount_cents) commitLine(line, { unit: v }); }}
                              className={`${inputCls} w-24 ml-1`} />
                          </label>
                          <label className="text-[11px] text-gray-500">Disc $
                            <input type="number" min="0" step="0.01" defaultValue={(line.line_discount_cents / 100).toFixed(2)} disabled={readOnly}
                              onBlur={(e) => { const v = Number(e.target.value) || 0; if (toCents(v) !== line.line_discount_cents) commitLine(line, { lineDiscount: v }); }}
                              className={`${inputCls} w-24 ml-1`} />
                          </label>
                          <button
                            onClick={() => { if (!readOnly) commitLine(line, { taxable: !line.taxable }); }}
                            disabled={readOnly}
                            className={`text-[11px] font-medium px-2 py-1.5 rounded-lg border ${line.taxable ? 'bg-blue-50 border-blue-200 text-blue-600' : 'bg-gray-50 border-gray-200 text-gray-500'} disabled:opacity-60`}
                          >
                            {line.taxable ? 'Taxable' : 'Tax exempt'}
                          </button>
                          <span className="ml-auto text-[13px] font-semibold text-gray-900">${fmt(line.total_cents)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Discount + terms + notes */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <div className="text-[12px] font-semibold text-gray-700 mb-1">Invoice discount</div>
                    <select value={discountType} disabled={readOnly} onChange={(e) => setDiscountType(e.target.value as any)} onBlur={commitHeader} className={inputCls}>
                      <option value="none">None</option>
                      <option value="percentage">Percentage</option>
                      <option value="fixed">Fixed $</option>
                    </select>
                  </div>
                  {discountType !== 'none' && (
                    <input type="number" min="0" step={discountType === 'percentage' ? '1' : '0.01'} value={discountValue} disabled={readOnly}
                      onChange={(e) => setDiscountValue(e.target.value)} onBlur={commitHeader}
                      placeholder={discountType === 'percentage' ? '%' : '$'} className={`${inputCls} w-28`} />
                  )}
                  <div className="ml-auto">
                    <div className="text-[12px] font-semibold text-gray-700 mb-1">Payment terms</div>
                    <select value={terms} disabled={readOnly} onChange={(e) => setTerms(e.target.value)} className={inputCls}>
                      {TERMS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <div className="text-[12px] font-semibold text-gray-700 mb-1">Notes</div>
                  <textarea value={notes} disabled={readOnly} onChange={(e) => setNotes(e.target.value)} onBlur={commitHeader} rows={2} className={`${inputCls} w-full`} placeholder="Notes shown on the invoice…" />
                </div>
              </div>

              {/* Totals */}
              {invoice && (
                <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-1 text-[13px]">
                  <div className="flex justify-between text-gray-600"><span>Subtotal</span><span>${fmt(invoice.subtotal_cents)}</span></div>
                  {invoice.discount_cents > 0 && <div className="flex justify-between text-gray-600"><span>Discount</span><span>-${fmt(invoice.discount_cents)}</span></div>}
                  {(invoice.tax_breakdown || []).map((t, i) => (
                    <div key={i} className="flex justify-between text-gray-600"><span>{t.name}</span><span>${fmt(t.amount_cents)}</span></div>
                  ))}
                  {invoice.tax_cents > 0 && (invoice.tax_breakdown || []).length === 0 && <div className="flex justify-between text-gray-600"><span>Tax</span><span>${fmt(invoice.tax_cents)}</span></div>}
                  <div className="flex justify-between text-gray-900 font-bold text-[15px] pt-1 border-t border-gray-100 mt-1"><span>Total</span><span>${fmt(invoice.total_cents)}</span></div>
                </div>
              )}

              {error && <div className="text-[13px] text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-200 bg-white shrink-0" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
          <div className="w-full max-w-3xl mx-auto flex items-center justify-between">
            {readOnly ? (
              <button onClick={onClose} className="ml-auto px-4 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-semibold hover:bg-blue-700">Close</button>
            ) : (
              <>
                <button onClick={handleDiscard} disabled={busy} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-[13px] font-medium hover:bg-gray-50 disabled:opacity-50">Cancel</button>
                <div className="flex items-center gap-2">
                  {busy && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
                  <button onClick={handleSaveDraft} disabled={busy} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-[13px] font-medium hover:bg-gray-50 disabled:opacity-50">Save draft</button>
                  <button onClick={handleIssue} disabled={busy || loading} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white text-[14px] font-semibold hover:bg-blue-700 disabled:opacity-60">
                    <Send className="h-4 w-4" /> Issue invoice
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {customerPickerOpen && (
        <SelectCustomerModal isOpen={customerPickerOpen} onClose={() => setCustomerPickerOpen(false)} onSelect={handleSelectCustomer} baseZIndex={100060} />
      )}
      {servicePickerOpen && (
        <SelectServiceModal isOpen={servicePickerOpen} onClose={() => setServicePickerOpen(false)} onAdd={handleAddService} closeOnAdd />
      )}
    </div>
  );

  return createPortal(content, document.body);
}
